import { useState } from "react";
import { trpc } from "../../../lib/api/trpc";

export type CollectionItem = {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  documentCount: number;
  createdAt: string;
};

export const useCollections = () => {
  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError, error, refetch } =
    trpc.collections.list.useQuery();

  return {
    collections: (data as CollectionItem[]) || [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCreateCollection = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useContext();

  const createCollection = async (input: {
    name: string;
    description?: string;
    color?: string;
  }) => {
    setIsCreating(true);
    setError(null);
    try {
      // @ts-ignore - tRPC type inference through `as any` router cast
      const result = await utils.client.collections.create.mutate(input);
      // @ts-ignore
      await utils.collections.list.invalidate();
      setIsCreating(false);
      return result;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create collection";
      setError(message);
      setIsCreating(false);
      throw err;
    }
  };

  return { createCollection, isCreating, error };
};

export const useDeleteCollection = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const utils = trpc.useContext();

  const deleteCollection = async (id: string) => {
    setIsDeleting(true);
    try {
      // @ts-ignore - tRPC type inference through `as any` router cast
      await utils.client.collections.delete.mutate({ id });
      // @ts-ignore
      await utils.collections.list.invalidate();
    } finally {
      setIsDeleting(false);
    }
  };

  return { deleteCollection, isDeleting };
};

export const useBulkAddToCollection = () => {
  const [isAdding, setIsAdding] = useState(false);
  const utils = trpc.useContext();

  const addDocuments = async (
    collectionId: string,
    documentIds: string[],
  ): Promise<{ added: number; notAdded: number }> => {
    setIsAdding(true);
    try {
      // @ts-ignore - tRPC type inference through `as any` router cast
      const result = await utils.client.collections.addDocuments.mutate({
        collectionId,
        documentIds,
      });
      // @ts-ignore
      await utils.collections.list.invalidate();
      // @ts-ignore
      await utils.documents.list.invalidate();
      return result as { added: number; notAdded: number };
    } finally {
      setIsAdding(false);
    }
  };

  return { addDocuments, isAdding };
};

export const useToggleDocumentCollection = () => {
  const [isToggling, setIsToggling] = useState(false);
  const utils = trpc.useContext();

  const addToCollection = async (collectionId: string, documentId: string) => {
    setIsToggling(true);
    try {
      // @ts-ignore - tRPC type inference through `as any` router cast
      await utils.client.collections.addDocument.mutate({
        collectionId,
        documentId,
      });
      // @ts-ignore
      await utils.collections.list.invalidate();
      // @ts-ignore
      await utils.documents.list.invalidate();
    } finally {
      setIsToggling(false);
    }
  };

  const removeFromCollection = async (
    collectionId: string,
    documentId: string,
  ) => {
    setIsToggling(true);
    try {
      // @ts-ignore - tRPC type inference through `as any` router cast
      await utils.client.collections.removeDocument.mutate({
        collectionId,
        documentId,
      });
      // @ts-ignore
      await utils.collections.list.invalidate();
      // @ts-ignore
      await utils.documents.list.invalidate();
    } finally {
      setIsToggling(false);
    }
  };

  return { addToCollection, removeFromCollection, isToggling };
};
